import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  HostListener,
} from '@angular/core';
import { ColumnConfig, FilterConfig, JobPost } from '../../interfaces/job-post';

@Component({
  selector: 'app-custom-table',
  templateUrl: './custom-table.component.html',
  styleUrls: ['./custom-table.component.css'],
})
export class CustomTableComponent implements OnChanges {
  @Input() jobs: JobPost[] = [];
  @Input() columns: ColumnConfig[] = [];
  @Input() filters: FilterConfig[] = [];
  @Input() searchKey: string = '';
  @Output() filterChanged = new EventEmitter<{ key: string; value: string }>();
  @Input() title = '';
  @Input() showPostButton = true;
  @Input() postButtonLabel = '';
  @Input() pageSize = 10;
  @Input() serverSidePagination = false;
  @Input() totalElements = 0;
  @Input() serverPage = 1;
  @Input() externalSearchQuery = '';

  // ── Action button visibility (per table instance) ──
  @Input() showViewButton = false;
  @Input() showDeleteButton = false;

  @Output() postClicked = new EventEmitter<void>();
  @Output() editClicked = new EventEmitter<JobPost>();
  @Output() viewClicked = new EventEmitter<JobPost>();
  @Output() deleteClicked = new EventEmitter<JobPost>();
  @Output() pageChanged = new EventEmitter<number>();
  @Output() searchChanged = new EventEmitter<string>();
  @Output() actionClicked = new EventEmitter<{ action: string; item: any }>();
  searchQuery = '';
  activeFilters: Record<string, string> = {};
  openDropdown: Record<string, boolean> = {};
  currentPage = 1;

  // ── Mobile bottom sheet state ──
  filterSheetOpen = false;

  ngOnChanges() {
    this.filters.forEach((f) => {
      if (!(f.key in this.activeFilters)) this.activeFilters[f.key] = 'All';
      if (!(f.key in this.openDropdown)) this.openDropdown[f.key] = false;
    });
    if (!this.serverSidePagination) {
      this.currentPage = 1;
    }
    if (this.externalSearchQuery !== undefined) {
      this.searchQuery = this.externalSearchQuery;
    }
  }

  get activeFilterCount(): number {
    return this.filters.filter(
      (f) => this.activeFilters[f.key] && this.activeFilters[f.key] !== 'All',
    ).length;
  }

  openFilterSheet(): void {
    this.filterSheetOpen = true;
  }

  closeFilterSheet(): void {
    this.filterSheetOpen = false;
  }

  clearAllFilters(): void {
    this.filters.forEach((f) => (this.activeFilters[f.key] = 'All'));
    this.currentPage = 1;
  }

  removeChip(filterKey: string): void {
    this.activeFilters[filterKey] = 'All';
    this.currentPage = 1;
  }

  get filteredJobs(): JobPost[] {
    return this.jobs.filter((job) => {
      if (this.searchKey && this.searchQuery) {
        const val = (job[this.searchKey] ?? '').toString().toLowerCase();
        if (!val.includes(this.searchQuery.toLowerCase())) return false;
      }
      for (const f of this.filters) {
        const selected = this.activeFilters[f.key];
        if (selected && selected !== 'All' && job[f.key] !== selected)
          return false;
      }
      return true;
    });
  }

  get pagedJobs(): JobPost[] {
    if (this.serverSidePagination) return this.filteredJobs;
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredJobs.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    if (this.serverSidePagination) {
      return Math.ceil(this.totalElements / this.pageSize);
    }
    return Math.ceil(this.filteredJobs.length / this.pageSize);
  }

  get firstItem(): number {
    if (this.serverSidePagination) {
      return this.totalElements === 0
        ? 0
        : (this.serverPage - 1) * this.pageSize + 1;
    }
    return this.filteredJobs.length === 0
      ? 0
      : (this.currentPage - 1) * this.pageSize + 1;
  }

  get lastItem(): number {
    if (this.serverSidePagination) {
      return Math.min(this.serverPage * this.pageSize, this.totalElements);
    }
    return Math.min(this.currentPage * this.pageSize, this.filteredJobs.length);
  }

  get activePage(): number {
    return this.serverSidePagination ? this.serverPage : this.currentPage;
  }

  get pageNumbers(): number[] {
    const total = this.totalPages;
    const cur = this.activePage;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages: number[] = [1];
    if (cur > 3) pages.push(-1);
    for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) {
      pages.push(i);
    }
    if (cur < total - 2) pages.push(-1);
    pages.push(total);
    return pages;
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    if (this.serverSidePagination) {
      this.pageChanged.emit(page - 1);
    } else {
      this.currentPage = page;
    }
  }

  onSearchChange() {
    this.currentPage = 1;
    this.searchChanged.emit(this.searchQuery);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.currentPage = 1;
    this.searchChanged.emit('');
  }

  toggleDropdown(key: string, event: Event) {
    event.stopPropagation();
    const wasOpen = this.openDropdown[key];
    Object.keys(this.openDropdown).forEach(
      (k) => (this.openDropdown[k] = false),
    );
    this.openDropdown[key] = !wasOpen;
  }

  selectOption(filterKey: string, option: string, event: Event) {
    event.stopPropagation();
    this.activeFilters[filterKey] = option;
    this.openDropdown[filterKey] = false;
    this.currentPage = 1;
    this.filterChanged.emit({ key: filterKey, value: option });
  }

  getBadgeClass(col: ColumnConfig, value: string): string {
    return col.badgeColorMap?.[value] ?? 'badge-default';
  }

  onPostVacancy() {
    this.postClicked.emit();
  }
  onEdit(job: JobPost) {
    this.editClicked.emit(job);
  }
  onView(job: JobPost) {
    this.viewClicked.emit(job);
  }
  onDelete(job: JobPost) {
    this.deleteClicked.emit(job);
  }
  triggerAction(action: string, item: any) {
    this.actionClicked.emit({ action, item });
  }

  @HostListener('document:click')
  closeAllDropdowns() {
    Object.keys(this.openDropdown).forEach(
      (k) => (this.openDropdown[k] = false),
    );
  }
}
